import React, { useEffect, useState } from "react";
import { Box, Chip, Stack, Tooltip } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import type { HelpDeskAttachment } from "../../Models/helpDesk";
import { getAttachmentBlob } from "../../Api/helpDesk";
import color from "../../Constants/colors";

export const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const isImage = (attachment: HelpDeskAttachment) =>
  attachment.content_type.startsWith("image/");

// Attachments need the bearer token, so they are fetched as blobs instead of
// linked directly. Images and PDFs open in a new tab, other files download.
const openAttachment = async (attachment: HelpDeskAttachment) => {
  const blob = await getAttachmentBlob(attachment.id);
  const url = URL.createObjectURL(blob);
  if (isImage(attachment) || attachment.content_type === "application/pdf") {
    window.open(url, "_blank");
  } else {
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.filename;
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const ImageThumb: React.FC<{ attachment: HelpDeskAttachment }> = ({
  attachment,
}) => {
  const [src, setSrc] = useState<string>();

  useEffect(() => {
    let url: string | undefined;
    let cancelled = false;
    getAttachmentBlob(attachment.id)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.id]);

  return (
    <Tooltip title={attachment.filename} arrow placement="top">
      <Box
        component="button"
        type="button"
        onClick={() => openAttachment(attachment)}
        sx={{
          width: 96,
          height: 72,
          p: 0,
          borderRadius: "8px",
          border: `1px solid ${color.gray200}`,
          bgcolor: color.gray50,
          overflow: "hidden",
          cursor: "pointer",
          display: "block",
        }}
      >
        {src && (
          <Box
            component="img"
            src={src}
            alt={attachment.filename}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

type Props = {
  attachments: HelpDeskAttachment[];
};

const AttachmentList: React.FC<Props> = ({ attachments }) => {
  if (!attachments.length) return null;

  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {images.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
          {images.map((attachment) => (
            <ImageThumb key={attachment.id} attachment={attachment} />
          ))}
        </Stack>
      )}
      {files.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
          {files.map((attachment) => (
            <Chip
              key={attachment.id}
              icon={<InsertDriveFileOutlinedIcon />}
              label={`${attachment.filename} · ${formatFileSize(attachment.size)}`}
              size="small"
              onClick={() => openAttachment(attachment)}
              sx={{
                maxWidth: 260,
                bgcolor: `${color.white} !important`,
                color: `${color.primary_dark} !important`,
                border: `1px solid ${color.gray200}`,
                fontSize: 11,
                fontWeight: 600,
                "& .MuiChip-icon": { color: color.secondary, fontSize: 16 },
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default AttachmentList;
